import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../../firebase';
import './emailMarketing.css';

const RR_ORIGIN = 'https://redefinereach.com';
const RR_EMBED_URL = `${RR_ORIGIN}/embed/sso`;

function EmailMarketing() {
  const iframeRef = useRef(null);
  const tokenRef = useRef('');
  const readyRef = useRef(false);
  const sentRef = useRef(false);
  const [iframeSrc, setIframeSrc] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      navigate('/login', { replace: true });
      return undefined;
    }

    const sendToken = () => {
      if (sentRef.current || !readyRef.current || !tokenRef.current) return;
      const frame = iframeRef.current;
      if (!frame || !frame.contentWindow) return;
      try {
        frame.contentWindow.postMessage(
          { type: 'redefine-reach-sso', customToken: tokenRef.current },
          RR_ORIGIN
        );
        sentRef.current = true;
      } catch {
        frame.src = `${RR_EMBED_URL}#token=${encodeURIComponent(tokenRef.current)}`;
        sentRef.current = true;
      }
    };

    const onMessage = (event) => {
      if (event.origin !== RR_ORIGIN) return;
      if (!event.data || event.data.type !== 'redefine-reach-sso-ready') return;
      readyRef.current = true;
      sendToken();
    };

    window.addEventListener('message', onMessage);

    const ac = new AbortController();
    (async () => {
      try {
        const idToken = await user.getIdToken(true);
        const res = await fetch('/api/email-marketing/sso', {
          method: 'POST',
          headers: { 'X-JZ-ID-Token': idToken },
          signal: ac.signal,
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (res.status === 401) {
            navigate('/login', { replace: true });
            return;
          }
          throw new Error(body.error || `Could not open email marketing (${res.status})`);
        }
        if (!body.customToken) {
          throw new Error('Could not open email marketing.');
        }
        tokenRef.current = body.customToken;
        sendToken();
      } catch (err) {
        if (err.name === 'AbortError') return;
        setError(err.message || 'Could not open email marketing.');
      }
    })();

    setIframeSrc(RR_EMBED_URL);

    return () => {
      ac.abort();
      window.removeEventListener('message', onMessage);
    };
  }, [navigate]);

  return (
    <div className="email-marketing-container">
      {error ? (
        <div className="email-marketing-error" role="alert">
          {error}
        </div>
      ) : null}
      {!error && iframeSrc ? (
        <iframe
          ref={iframeRef}
          id="redefine-reach"
          src={iframeSrc}
          title="Email marketing"
          className="email-marketing-frame"
        />
      ) : null}
    </div>
  );
}

export default EmailMarketing;
